'use client'

import React, { useState, useEffect, useRef, Suspense } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { Menu, X, ShoppingBag, Tag, Sparkles, Store, Flower2, ChevronDown, ArrowRight, Zap, Search } from 'lucide-react'
import { CategoryData, EventResponse, Merchant, Promotion } from '@/services/dataTypes'
import { getBaseImageUrl, getEventsHref, getMerchantHref, getProductMerchantHref, getPromotionHref } from '@/constants/hooks'
import SearchBar from './SearchBar'

interface Props {
    unique_id: string; merchantData: Merchant[]; categories: CategoryData[] | any
    headerPromoMerchant: Merchant[] | null; companyDomain: string; mer_slug: string
    mer_slug_type: string; promo_slug: string; cat_slug: string; blog_url?: string
    blog_title?: string; company_logo: string | null; events: EventResponse[]; promotions: Promotion[]
    productMerchants?: any[]
}

const NAV_BG = '#FFFFFF'
const NAV_BORDER = 'rgba(45,62,45,0.1)'
const ACCENT = '#F5C542'
const ACCENT_SOFT = 'rgba(245,197,66,0.12)'
const TEXT_PRIMARY = '#2D3E2D'
const TEXT_MUTED = 'rgba(45,62,45,0.65)'
const DROPDOWN_BG = '#FFFFFF'
const DROPDOWN_BORDER = 'rgba(45,62,45,0.1)'
const ITEM_HOVER = 'rgba(245,197,66,0.08)'

const Navbar = ({ unique_id, merchantData, categories, companyDomain, mer_slug, mer_slug_type, cat_slug, blog_url, blog_title, company_logo, events, promotions, promo_slug, productMerchants }: Props) => {
    const [mobileOpen, setMobileOpen] = useState(false)
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
    const [mobileExpanded, setMobileExpanded] = useState<string | null>(null)
    const [scrolled, setScrolled] = useState(false)
    const dropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const pathname = usePathname()
    const logo = getBaseImageUrl(companyDomain, company_logo, '')

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20)
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])
    useEffect(() => { setMobileOpen(false); setMobileExpanded(null) }, [pathname])
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : ''
        return () => { document.body.style.overflow = '' }
    }, [mobileOpen])

    const navItems = [
        { name: 'Categories', href: '/category', icon: Tag, hasDropdown: true },
        { name: 'Stores', href: '/all-stores/A', icon: Store, hasDropdown: true },
        { name: 'Products', href: '/all-products', icon: ShoppingBag, hasDropdown: true },
        ...(Array.isArray(promotions) && promotions.length > 0 ? [{ name: 'Promotions', href: `/${promo_slug}`, icon: Sparkles, hasDropdown: true }] : []),
        ...(Array.isArray(events) && events.length > 0 ? [{ name: 'Events', href: '/events', icon: Flower2, hasDropdown: true }] : []),
        ...(blog_title && blog_url ? [{ name: blog_title, href: blog_url, icon: null, hasDropdown: false, isExternal: true }] : []),
    ]

    const isActive = (href: string) => pathname === href || (href !== '/' && pathname.startsWith(href.replace('/A', '')))
    const open = (name: string) => { if (dropdownTimer.current) clearTimeout(dropdownTimer.current); setActiveDropdown(name) }
    const close = () => { dropdownTimer.current = setTimeout(() => setActiveDropdown(null), 150) }

    return (
        <>
            <header className="sticky top-0 z-[100]">
                <div className={`transition-all duration-300 ${scrolled ? 'shadow-xl' : ''}`}
                    style={{ background: NAV_BG, borderBottom: `1px solid ${NAV_BORDER}` }}>
                    <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between gap-6">

                        {/* Logo */}
                        <Link href="/" className="flex-shrink-0 flex items-center gap-2.5 group" aria-label="Home">
                            {company_logo
                                ? <Image src={logo} alt="Logo" width={110} height={32} className="h-8 w-auto object-contain transition-transform group-hover:scale-105" priority />
                                : <span className="flex items-center gap-2 font-black text-lg tracking-tight" style={{ color: TEXT_PRIMARY }}>
                                    <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: ACCENT }}>
                                        <Zap size={15} style={{ color: '#fff' }} />
                                    </div>
                                    CouponBase
                                </span>
                            }
                        </Link>

                        {/* Desktop nav */}
                        <nav className="hidden lg:flex items-center justify-center gap-1.5 flex-1 mx-2" aria-label="Main navigation">
                            {navItems.map(item => (
                                <div key={item.name} className="relative"
                                    onMouseEnter={() => item.hasDropdown && open(item.name)}
                                    onMouseLeave={() => item.hasDropdown && close()}>
                                    {item.hasDropdown ? (
                                        <button
                                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                                            style={{
                                                color: isActive(item.href) ? ACCENT : TEXT_MUTED,
                                                background: isActive(item.href) ? ACCENT_SOFT : 'transparent',
                                                fontWeight: isActive(item.href) ? 600 : 500,
                                            }}
                                            aria-expanded={activeDropdown === item.name}>
                                            {item.icon && <item.icon size={14} strokeWidth={1.8} />}
                                            {item.name}
                                            <ChevronDown size={12} className={`ml-0.5 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                                        </button>
                                    ) : (
                                        <Link
                                            href={item.href}
                                            {...(item.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all"
                                            style={{
                                                color: isActive(item.href) ? ACCENT : TEXT_MUTED,
                                                background: isActive(item.href) ? ACCENT_SOFT : 'transparent',
                                            }}>
                                            {item.name}
                                        </Link>
                                    )}

                                    {/* Dropdown */}
                                    {item.hasDropdown && activeDropdown === item.name && (
                                        <div className="absolute top-[calc(100%+8px)] left-0 p-2 z-[200] rounded-2xl animate-fadeInUp"
                                            style={{
                                                background: DROPDOWN_BG,
                                                border: `1px solid ${DROPDOWN_BORDER}`,
                                                boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
                                                minWidth: item.name === 'Categories' || item.name === 'Stores' ? '340px'
                                                    : item.name === 'Products' ? '220px'
                                                    : item.name === 'Promotions' || item.name === 'Events' ? '260px'
                                                    : '240px',
                                            }}>
                                            <style dangerouslySetInnerHTML={{
                                                __html: `
                                                @keyframes fadeInUp {
                                                    from { opacity: 0; transform: translateY(6px); }
                                                    to { opacity: 1; transform: translateY(0); }
                                                }
                                                .animate-fadeInUp { animation: fadeInUp 0.18s ease; }
                                                .dd-item { color: ${TEXT_MUTED}; transition: all 0.15s; display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: 10px; font-size: 13px; font-weight: 500; }
                                                .dd-item:hover { background: ${ITEM_HOVER}; color: ${TEXT_PRIMARY}; }
                                            `}} />
                                            <div className="text-[10px] font-bold uppercase tracking-wider px-2 pb-2 mb-1"
                                                style={{ color: 'rgba(248,250,252,0.25)', borderBottom: `1px solid ${DROPDOWN_BORDER}` }}>
                                                {item.name}
                                            </div>

                                            {item.name === 'Categories' && (
                                                <div className="grid grid-cols-2 gap-0.5">
                                                    {categories?.slice(0, 8).map((cat: any, i: number) => (
                                                        <Link key={i} href={`/${cat?.url}`} className="dd-item">
                                                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ACCENT }} />
                                                            <span>{cat.name}</span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                            {item.name === 'Stores' && (
                                                <div className="grid grid-cols-2 gap-0.5">
                                                    {merchantData?.slice(0, 6).map((m, i) => (
                                                        <Link key={i} href={getMerchantHref(m, mer_slug, mer_slug_type)} className="dd-item">
                                                            <div className="w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                                                                style={{ background: 'rgba(255,255,255,0.08)', border: `1px solid ${DROPDOWN_BORDER}` }}>
                                                                <Image src={getBaseImageUrl(companyDomain, m.merchant_logo, '')} alt="" width={14} height={14} className="object-contain" />
                                                            </div>
                                                            <span>{m.merchant_name}</span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                            {item.name === 'Products' && (
                                                <div className="flex flex-col gap-0.5">
                                                    <Link href="/products" className="dd-item"><ShoppingBag size={13} style={{ color: ACCENT }} />Brand Products</Link>
                                                    <Link href="/all-products" className="dd-item"><Store size={13} style={{ color: ACCENT }} />All Products</Link>
                                                </div>
                                            )}
                                            {item.name === 'Promotions' && (
                                                <div className="flex flex-col gap-0.5">
                                                    {promotions?.slice(0, 6).map((p, i) => (
                                                        <Link key={i} href={getPromotionHref(p, promo_slug)} className="dd-item">
                                                            <Sparkles size={13} style={{ color: ACCENT }} />{p.name}
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                            {item.name === 'Events' && (
                                                <div className="flex flex-col gap-0.5">
                                                    {events?.slice(0, 6).map((e, i) => (
                                                        <Link key={i} href={getEventsHref(e, mer_slug_type)} className="dd-item">
                                                            <Flower2 size={13} style={{ color: ACCENT }} />{e.name}
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                            <Link href={item.href}
                                                className="flex items-center justify-center gap-1.5 mt-2 pt-2 text-[11px] font-semibold rounded-xl py-2 transition-all hover:gap-2"
                                                style={{ borderTop: `1px solid ${DROPDOWN_BORDER}`, color: ACCENT }}>
                                                View all {item.name} <ArrowRight size={11} />
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </nav>

                        {/* Search */}
                        <div className="hidden lg:block w-72 flex-shrink-0">
                            <Suspense fallback={<div className="w-full h-10 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />}>
                                <SearchBar companyId={unique_id} mer_slug={mer_slug} slug_type={mer_slug_type} cat_slug={cat_slug} domain={companyDomain} />
                            </Suspense>
                        </div>

                        {/* Mobile toggle */}
                        <button onClick={() => setMobileOpen(true)}
                            className="lg:hidden ml-auto p-2 rounded-lg transition"
                            style={{ background: 'rgba(255,255,255,0.08)', color: TEXT_PRIMARY }}
                            aria-label="Open menu">
                            <Menu size={20} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile overlay */}
            {mobileOpen && (
                <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm"
                    onClick={() => setMobileOpen(false)} aria-hidden="true" />
            )}

            {/* Mobile drawer */}
            <div className={`fixed top-0 right-0 h-full w-[80vw] max-w-[320px] z-[200] flex flex-col transition-transform duration-300 ease-out ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
                style={{ background: NAV_BG, borderTopLeftRadius: 24, borderBottomLeftRadius: 24, boxShadow: '-8px 0 40px rgba(0,0,0,0.1)' }}
                role="navigation" aria-label="Mobile navigation">

                <div className="flex items-center justify-between px-5 py-4 flex-shrink-0"
                    style={{ borderBottom: `1px solid ${DROPDOWN_BORDER}` }}>
                    <Link href="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2">
                        {company_logo
                            ? <Image src={logo} alt="Logo" width={80} height={24} className="h-6 w-auto object-contain" />
                            : <span className="font-black text-lg flex items-center gap-2" style={{ color: TEXT_PRIMARY }}>
                                <div className="w-7 h-7 rounded-xl flex items-center justify-center" style={{ background: ACCENT }}>
                                    <Zap size={13} style={{ color: '#fff' }} />
                                </div>
                                CouponBase
                            </span>
                        }
                    </Link>
                    <button onClick={() => setMobileOpen(false)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center transition hover:bg-slate-100"
                        style={{ color: TEXT_PRIMARY }}>
                        <X size={16} />
                    </button>
                </div>

                <div className="px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${DROPDOWN_BORDER}` }}>
                    <Suspense fallback={<div className="w-full h-10 rounded-lg animate-pulse" style={{ background: 'rgba(45,62,45,0.05)' }} />}>
                        <SearchBar companyId={unique_id} mer_slug={mer_slug} slug_type={mer_slug_type} cat_slug={cat_slug} domain={companyDomain} />
                    </Suspense>
                </div>

                <div className="flex-1 overflow-y-auto py-2">
                    {navItems.map(item => (
                        <div key={item.name} style={{ borderBottom: `1px solid ${DROPDOWN_BORDER}` }}>
                            {item.hasDropdown ? (
                                <>
                                    <button
                                        onClick={() => setMobileExpanded(mobileExpanded === item.name ? null : item.name)}
                                        className="flex items-center justify-between w-full px-5 py-3.5 text-sm font-semibold text-left bg-transparent border-none cursor-pointer"
                                        style={{ color: isActive(item.href) ? ACCENT : TEXT_PRIMARY }}>
                                        <span className="flex items-center gap-3">
                                            {item.icon && (
                                                <span className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                                                    style={{ background: isActive(item.href) ? ACCENT_SOFT : 'rgba(255,255,255,0.07)' }}>
                                                    <item.icon size={15} style={{ color: isActive(item.href) ? ACCENT : TEXT_MUTED }} />
                                                </span>
                                            )}
                                            {item.name}
                                        </span>
                                        <ChevronDown size={14} style={{ color: TEXT_MUTED, flexShrink: 0 }}
                                            className={`transition-transform duration-200 ${mobileExpanded === item.name ? 'rotate-180' : ''}`} />
                                    </button>

                                    {mobileExpanded === item.name && (
                                        <div className="mx-4 mb-3 rounded-2xl overflow-hidden"
                                            style={{ background: 'rgba(45,62,45,0.03)', border: `1px solid ${DROPDOWN_BORDER}` }}>
                                            <div className="px-4 py-2.5 text-[10px] font-black uppercase tracking-wider"
                                                style={{ color: TEXT_MUTED, borderBottom: `1px solid ${DROPDOWN_BORDER}` }}>
                                                {item.name}
                                            </div>
                                            <div className="p-3">
                                                {item.name === 'Categories' && (
                                                    <div className="grid grid-cols-2 gap-0.5">
                                                        {categories?.slice(0, 8).map((cat: any, i: number) => (
                                                            <Link key={i} href={`/${cat?.url}`} onClick={() => setMobileOpen(false)}
                                                                className="flex items-center gap-2 px-2.5 py-2 rounded-xl text-sm font-medium"
                                                                style={{ color: TEXT_MUTED }}>
                                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ACCENT }} />
                                                                <span className="truncate">{cat.name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                                {item.name === 'Stores' && (
                                                    <div className="grid grid-cols-2 gap-0.5">
                                                        {merchantData?.slice(0, 6).map((m, i) => (
                                                            <Link key={i} href={getMerchantHref(m, mer_slug, mer_slug_type)} onClick={() => setMobileOpen(false)}
                                                                className="flex items-center gap-2 px-2.5 py-2 rounded-xl text-sm font-medium hover:bg-slate-50 transition"
                                                                style={{ color: TEXT_MUTED }}>
                                                                <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                                                                    style={{ background: '#FFFFFF', border: `1px solid ${DROPDOWN_BORDER}` }}>
                                                                    <Image src={getBaseImageUrl(companyDomain, m.merchant_logo, '')} alt="" width={16} height={16} className="object-contain" />
                                                                </div>
                                                                <span className="truncate">{m.merchant_name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                                {item.name === 'Products' && (
                                                    <div className="flex flex-col gap-0.5">
                                                        <Link href="/products" onClick={() => setMobileOpen(false)}
                                                            className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-medium"
                                                            style={{ color: TEXT_MUTED }}>
                                                            <ShoppingBag size={14} style={{ color: ACCENT }} /> Brand Products
                                                        </Link>
                                                        <Link href="/all-products" onClick={() => setMobileOpen(false)}
                                                            className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-medium"
                                                            style={{ color: TEXT_MUTED }}>
                                                            <Store size={14} style={{ color: ACCENT }} /> All Products
                                                        </Link>
                                                    </div>
                                                )}
                                                {item.name === 'Promotions' && (
                                                    <div className="flex flex-col gap-0.5">
                                                        {promotions?.slice(0, 6).map((p, i) => (
                                                            <Link key={i} href={getPromotionHref(p, promo_slug)} onClick={() => setMobileOpen(false)}
                                                                className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-medium"
                                                                style={{ color: TEXT_MUTED }}>
                                                                <Sparkles size={14} style={{ color: ACCENT }} />
                                                                <span className="truncate">{p.name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                                {item.name === 'Events' && (
                                                    <div className="flex flex-col gap-0.5">
                                                        {events?.slice(0, 6).map((e, i) => (
                                                            <Link key={i} href={getEventsHref(e, mer_slug_type)} onClick={() => setMobileOpen(false)}
                                                                className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-medium"
                                                                style={{ color: TEXT_MUTED }}>
                                                                <Flower2 size={14} style={{ color: ACCENT }} />
                                                                <span className="truncate">{e.name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <Link href={item.href} onClick={() => setMobileOpen(false)}
                                                className="flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold"
                                                style={{ borderTop: `1px solid ${DROPDOWN_BORDER}`, color: ACCENT }}>
                                                View all {item.name} <ArrowRight size={11} />
                                            </Link>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Link href={item.href}
                                    {...(item.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center gap-3 w-full px-5 py-3.5 text-sm font-semibold transition"
                                    style={{ color: isActive(item.href) ? ACCENT : TEXT_PRIMARY }}>
                                    {item.icon && (
                                        <span className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ background: 'rgba(255,255,255,0.07)' }}>
                                            <item.icon size={15} style={{ color: TEXT_MUTED }} />
                                        </span>
                                    )}
                                    {item.name}
                                </Link>
                            )}
                        </div>
                    ))}
                </div>

                <div className="px-4 py-4 flex-shrink-0" style={{ borderTop: `1px solid ${DROPDOWN_BORDER}` }}>
                    <Link href="/all-stores/A" onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-center gap-2 w-full py-3 text-sm font-bold rounded-xl transition hover:opacity-90"
                        style={{ background: ACCENT, color: '#fff' }}>
                        <Zap size={15} /> Explore Hot Deals
                    </Link>
                </div>
            </div>
        </>
    )
}

export default Navbar
